import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { onApproveAllWashouts } from "@/Washouts/api/PayrollClosing/subscriptions";
import { onRetractAllWashouts } from "@/Washouts/api/WashoutReview/subscriptions";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { IOnApproveAllWashouts, IOnRetractAllWashouts } from "@/Washouts/types/PayrollClosing/subscriptionCallbacks";
import { onApproveAllWashoutsCallback, onRetractAllWashoutsCallback } from "@/Washouts/helpers/PayrollClosing/subscriptionCallbacks";

const useApproveAllWashoutSubscription = ({
	locationId,
	commissionCycleId,
	payPeriodId,
}: {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
}) => {
	const { addSubscription, getSubscription, deleteSubscription } = useSubscriptions();
	const queryClient = useQueryClient();

	const approveAllWashoutsSubscription = getSubscription(["approveAllWashoutsSubscription", commissionCycleId, payPeriodId]);
	approveAllWashoutsSubscription?.subscriptions?.onApproveAllWashouts?.setCallback(
		(response: GraphqlSubscriptionResponse<"onApproveAllWashouts", IOnApproveAllWashouts>) => {
			onApproveAllWashoutsCallback({ queryClient, response });
		}
	);

	const retractAllWashoutsSubscription = getSubscription(["retractAllWashoutsSubscription", commissionCycleId, payPeriodId]);
	retractAllWashoutsSubscription?.subscriptions?.onRetractAllWashouts?.setCallback(
		(response: GraphqlSubscriptionResponse<"onRetractAllWashouts", IOnRetractAllWashouts>) => {
			onRetractAllWashoutsCallback({ queryClient, response });
		}
	);

	useEffect(() => {
		addSubscription(
			["approveAllWashoutsSubscription", commissionCycleId, payPeriodId],
			[
				{
					context: "onApproveAllWashouts",
					query: onApproveAllWashouts,
					variables: {
						locationId,
						commissionCycleId,
						payPeriodId,
					},
				},
			]
		);

		addSubscription(
			["retractAllWashoutsSubscription", commissionCycleId, payPeriodId],
			[
				{
					context: "onRetractAllWashouts",
					query: onRetractAllWashouts,
					variables: {
						locationId,
						commissionCycleId,
						payPeriodId,
					},
				},
			]
		);

		return () => {
			deleteSubscription(["approveAllWashoutsSubscription", commissionCycleId, payPeriodId]);
			deleteSubscription(["retractAllWashoutsSubscription", commissionCycleId, payPeriodId]);
		};
	}, [locationId, commissionCycleId, payPeriodId]);
};

export default useApproveAllWashoutSubscription;
